import React, { useCallback, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  ButtonBase,
  Card,
  CardContent,
  Chip,
  Collapse,
  List,
  ListItemButton,
  Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { formatCurrency } from '../../../../shared/utils/currency';
import type {
  GangMemberSplit,
  FixedRateWorker,
} from '../../types/potDetails.types';

const stringToColor = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 45%, 55%)`;
};

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? '';
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

interface PotWorkersCardProps {
  gangMemberSplits: GangMemberSplit[];
  fixedRateWorkers: FixedRateWorker[];
  onWorkerClick?: (userId: string) => void;
}

interface WorkerGroup {
  key: string;
  label: string;
  icon: React.ElementType;
  count: number;
  totalAmount: number;
}

export function PotWorkersCard({
  gangMemberSplits,
  fixedRateWorkers,
  onWorkerClick,
}: PotWorkersCardProps) {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    new Set()
  );

  const toggleGroup = useCallback((key: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const fixedRateWorkerIds = useMemo(
    () => new Set(fixedRateWorkers.map(w => w.userId)),
    [fixedRateWorkers]
  );

  const leaderLookup = useMemo(
    () => new Map(gangMemberSplits.map(w => [w.userId, w.isLeader])),
    [gangMemberSplits]
  );

  const newMemberLookup = useMemo(
    () => new Map(gangMemberSplits.map(w => [w.userId, w.isNewMember])),
    [gangMemberSplits]
  );

  const percentageSplitWorkers = useMemo(
    () =>
      gangMemberSplits
        .filter(w => !fixedRateWorkerIds.has(w.userId))
        .sort((a, b) => {
          if (b.amount !== a.amount) return b.amount - a.amount;
          if (a.isLeader !== b.isLeader) return a.isLeader ? -1 : 1;
          return 0;
        }),
    [gangMemberSplits, fixedRateWorkerIds]
  );

  const fixedRateGangMembers = useMemo(
    () =>
      fixedRateWorkers
        .filter(w => w.isGangMember)
        .sort((a, b) => {
          if (b.totalAmount !== a.totalAmount)
            return b.totalAmount - a.totalAmount;
          const aIsLeader = leaderLookup.get(a.userId) ?? false;
          const bIsLeader = leaderLookup.get(b.userId) ?? false;
          if (aIsLeader !== bIsLeader) return aIsLeader ? -1 : 1;
          return 0;
        }),
    [fixedRateWorkers, leaderLookup]
  );

  const adHocWorkers = useMemo(
    () =>
      fixedRateWorkers
        .filter(w => !w.isGangMember)
        .sort((a, b) => b.totalAmount - a.totalAmount),
    [fixedRateWorkers]
  );

  const totalWorkers =
    percentageSplitWorkers.length +
    fixedRateGangMembers.length +
    adHocWorkers.length;

  if (totalWorkers === 0) return null;

  const groups: WorkerGroup[] = [];
  if (percentageSplitWorkers.length > 0) {
    groups.push({
      key: 'percentage',
      label: 'Percentage Split',
      icon: PeopleIcon,
      count: percentageSplitWorkers.length,
      totalAmount: percentageSplitWorkers.reduce((s, w) => s + w.amount, 0),
    });
  }
  if (fixedRateGangMembers.length > 0) {
    groups.push({
      key: 'fixed',
      label: 'Fixed Rate',
      icon: PersonIcon,
      count: fixedRateGangMembers.length,
      totalAmount: fixedRateGangMembers.reduce((s, w) => s + w.totalAmount, 0),
    });
  }
  if (adHocWorkers.length > 0) {
    groups.push({
      key: 'adhoc',
      label: 'Ad-hoc',
      icon: PersonAddIcon,
      count: adHocWorkers.length,
      totalAmount: adHocWorkers.reduce((s, w) => s + w.totalAmount, 0),
    });
  }

  return (
    <Card
      variant="outlined"
      data-testid="pot-workers-card"
      sx={{ height: '100%' }}
    >
      <CardContent
        sx={{
          p: 0,
          '&:last-child': { pb: 0 },
        }}
      >
        <Typography
          variant="h6"
          sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 3 }, pb: 1 }}
        >
          Workers ({totalWorkers})
        </Typography>

        <List disablePadding>
          {groups.map(group => {
            const expanded = !collapsedGroups.has(group.key);
            const GroupIcon = group.icon;
            return (
              <Box key={group.key}>
                <ButtonBase
                  onClick={() => toggleGroup(group.key)}
                  aria-expanded={expanded}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    width: '100%',
                    px: { xs: 2, sm: 3 },
                    py: 1,
                    justifyContent: 'flex-start',
                    bgcolor: 'action.selected',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <ChevronRightIcon
                    fontSize="small"
                    sx={{
                      color: 'text.secondary',
                      transition: 'transform 150ms ease',
                      transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    }}
                  />
                  <GroupIcon
                    sx={{
                      fontSize: 16,
                      color: 'text.secondary',
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="body2" fontWeight={600}>
                    {group.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {group.count}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ ml: 'auto' }}
                  >
                    {formatCurrency(group.totalAmount)}
                  </Typography>
                </ButtonBase>

                <Collapse in={expanded} unmountOnExit>
                  {group.key === 'percentage' &&
                    percentageSplitWorkers.map(worker => (
                      <PercentageWorkerListItem
                        key={worker.userId}
                        worker={worker}
                        onClick={onWorkerClick}
                      />
                    ))}
                  {group.key === 'fixed' &&
                    fixedRateGangMembers.map(worker => (
                      <FixedRateWorkerListItem
                        key={worker.userId}
                        worker={worker}
                        isLeader={leaderLookup.get(worker.userId) ?? false}
                        isNewMember={
                          newMemberLookup.get(worker.userId) ?? false
                        }
                        onClick={onWorkerClick}
                      />
                    ))}
                  {group.key === 'adhoc' &&
                    adHocWorkers.map(worker => (
                      <AdHocWorkerListItem
                        key={worker.userId}
                        worker={worker}
                        onClick={onWorkerClick}
                      />
                    ))}
                </Collapse>
              </Box>
            );
          })}
        </List>
      </CardContent>
    </Card>
  );
}

const PercentageWorkerListItem = React.memo(function PercentageWorkerListItem({
  worker,
  onClick,
}: {
  worker: GangMemberSplit;
  onClick?: (userId: string) => void;
}) {
  const percentageDisplay = `${Math.round(worker.percentage * 100)}%`;

  return (
    <ListItemButton
      onClick={() => onClick?.(worker.userId)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1.5,
        px: { xs: 2, sm: 3 },
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Avatar
        sx={{
          width: 32,
          height: 32,
          fontSize: '0.75rem',
          fontWeight: 600,
          bgcolor: stringToColor(worker.userName),
          flexShrink: 0,
        }}
      >
        {getInitials(worker.userName)}
      </Avatar>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {worker.userName}
          </Typography>
          {worker.isLeader && (
            <Chip
              label="Leader"
              size="small"
              color="primary"
              sx={{ height: 20, fontSize: '0.7rem', flexShrink: 0 }}
            />
          )}
          {worker.isNewMember && (
            <Chip
              label="New"
              size="small"
              color="warning"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.7rem', flexShrink: 0 }}
            />
          )}
          {worker.isFormerMember && (
            <Chip
              label="Former"
              size="small"
              color="warning"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.7rem', flexShrink: 0 }}
            />
          )}
        </Box>
        <Typography variant="caption" color="text.secondary">
          {percentageDisplay} split
        </Typography>
      </Box>

      <Typography
        variant="body2"
        fontWeight={600}
        sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
      >
        {formatCurrency(worker.amount)}
      </Typography>
    </ListItemButton>
  );
});

const FixedRateWorkerListItem = React.memo(function FixedRateWorkerListItem({
  worker,
  isLeader,
  isNewMember,
  onClick,
}: {
  worker: FixedRateWorker;
  isLeader: boolean;
  isNewMember: boolean;
  onClick?: (userId: string) => void;
}) {
  const calculation =
    worker.bonusAmount > 0
      ? `${formatCurrency(worker.rateValue)} × ${worker.unitsWorked} + ${formatCurrency(worker.bonusAmount)}`
      : `${formatCurrency(worker.rateValue)} × ${worker.unitsWorked}`;

  return (
    <ListItemButton
      onClick={() => onClick?.(worker.userId)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1.5,
        px: { xs: 2, sm: 3 },
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Avatar
        sx={{
          width: 32,
          height: 32,
          fontSize: '0.75rem',
          fontWeight: 600,
          bgcolor: stringToColor(worker.userName),
          flexShrink: 0,
        }}
      >
        {getInitials(worker.userName)}
      </Avatar>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {worker.userName}
          </Typography>
          {isLeader && (
            <Chip
              label="Leader"
              size="small"
              color="primary"
              sx={{ height: 20, fontSize: '0.7rem', flexShrink: 0 }}
            />
          )}
          {isNewMember && (
            <Chip
              label="New"
              size="small"
              color="warning"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.7rem', flexShrink: 0 }}
            />
          )}
          {worker.isFormerMember && (
            <Chip
              label="Former"
              size="small"
              color="warning"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.7rem', flexShrink: 0 }}
            />
          )}
        </Box>
        <Typography variant="caption" color="text.secondary">
          {calculation}
        </Typography>
      </Box>

      <Typography
        variant="body2"
        fontWeight={600}
        sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
      >
        {formatCurrency(worker.totalAmount)}
      </Typography>
    </ListItemButton>
  );
});

const AdHocWorkerListItem = React.memo(function AdHocWorkerListItem({
  worker,
  onClick,
}: {
  worker: FixedRateWorker;
  onClick?: (userId: string) => void;
}) {
  const calculation =
    worker.bonusAmount > 0
      ? `${formatCurrency(worker.rateValue)} × ${worker.unitsWorked} + ${formatCurrency(worker.bonusAmount)}`
      : `${formatCurrency(worker.rateValue)} × ${worker.unitsWorked}`;

  return (
    <ListItemButton
      onClick={() => onClick?.(worker.userId)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1.5,
        px: { xs: 2, sm: 3 },
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Avatar
        sx={{
          width: 32,
          height: 32,
          fontSize: '0.75rem',
          fontWeight: 600,
          bgcolor: stringToColor(worker.userName),
          flexShrink: 0,
        }}
      >
        {getInitials(worker.userName)}
      </Avatar>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {worker.userName}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {calculation}
        </Typography>
      </Box>

      <Typography
        variant="body2"
        fontWeight={600}
        sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
      >
        {formatCurrency(worker.totalAmount)}
      </Typography>
    </ListItemButton>
  );
});
